import { ref, type Ref } from 'vue'
import { api } from '@/lib/api'

export interface User {
  id: string
  user_id: string
  company_id: string
  email: string
  ppsn: string
  first_name: string
  surname: string
  date_of_birth: string
  marital_status: string
  annual_income: number | null
  paid_pension: number | null
  risk_profile: string | null
  pension_plan_id: string | null
  address1: string | null
  address2: string | null
  city: string | null
  region: string | null
  postal_code: string | null
  notes: string | null
  status: string | null
  country_id: string | null
  language_id: string | null
  last_ip: string | null
  email_verified_at: string | null
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
  answers: Record<string, any>[]
  taxback: number | null
  pension: number | null
  payments: Record<string, any>[]
  paye_a1_authorized: boolean
  paye_a1_authorized_at: string | null
  paye_a1_expiry_at: string | null
  opt_out_paye_auth: boolean
  form11: Record<string, any>[]
}

interface RegisterData {
  email: string
  details: {
    dob: string
    maritalStatus: string
    pensionPlanId: string
  }
}

interface EditProfileData {
  annual_income?: number
  paid_pension?: number
  ppsn?: string
  date_of_birth?: string
  marital_status?: string
  pension_plan_id?: string
  opt_out_paye_auth?: boolean
  first_name?: string
  surname?: string
}

interface AuthResponse {
  access_token: string
  refresh_token: string
  user: User
}

interface AuthComposable {
  user: Ref<User | null>
  loading: Ref<boolean>
  answers: Ref<Record<string, any>>
  login: (email: string, password: string, remember?: boolean) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  refreshProfile: () => Promise<void>
  initAuth: () => Promise<void>
  refreshToken: () => Promise<void>
  updateUser: (data: EditProfileData) => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (
    token: string,
    password: string,
    password_confirmation: string,
  ) => Promise<void>
}

const TOKEN_KEY = 'session'
const REFRESH_TOKEN_KEY = 'refresh_token'
const REMEMBER_ME_KEY = 'remember_me'

const user = ref<User | null>(null)
const loading = ref(true)
const answers = ref<Record<string, any>>({})

function setTokens(
  access_token: string,
  refresh_token?: string,
  remember: boolean = false,
) {
  const storage = remember ? localStorage : sessionStorage
  storage.setItem(TOKEN_KEY, access_token)
  if (refresh_token) {
    storage.setItem(REFRESH_TOKEN_KEY, refresh_token)
  }
  storage.setItem(REMEMBER_ME_KEY, String(remember))
  api.defaults.headers.common.Authorization = `Bearer ${access_token}`
}

function clearTokens() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(REMEMBER_ME_KEY)
  sessionStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(REFRESH_TOKEN_KEY)
  sessionStorage.removeItem(REMEMBER_ME_KEY)
  delete api.defaults.headers.common.Authorization
}

async function refreshToken() {
  const storage =
    localStorage.getItem(REMEMBER_ME_KEY) === 'true'
      ? localStorage
      : sessionStorage
  const refresh_token = storage.getItem(REFRESH_TOKEN_KEY)

  if (!refresh_token) {
    throw new Error('No refresh token')
  }

  try {
    const { data } = await api.post<AuthResponse>('/auth/refresh', {
      refresh_token,
    })
    setTokens(data.access_token, data.refresh_token, storage === localStorage)
    user.value = data.user
  } catch (error) {
    clearTokens()
    throw error
  }
}

async function login(
  email: string,
  password: string,
  remember: boolean = false,
) {
  const { data } = await api.post<AuthResponse>('/auth/login', {
    email,
    password,
  })
  setTokens(data.access_token, data.refresh_token, remember)
  if (data.user) {
    user.value = data.user
  } else {
    // If user data not included in login response, fetch it
    await refreshProfile()
  }
}

async function register(data: RegisterData) {
  const response = await api.post<AuthResponse>('/auth/register', data)
  setTokens(response.data.access_token, response.data.refresh_token)
  if (response.data.user) {
    user.value = response.data.user
  } else {
    await refreshProfile()
  }
}

async function updateUser(data: EditProfileData) {
  const { data: userData } = await api.post<User>('/edit-profile', data)
  user.value = userData
}

async function logout() {
  const storage =
    localStorage.getItem(REMEMBER_ME_KEY) === 'true'
      ? localStorage
      : sessionStorage
  const refresh_token = storage.getItem(REFRESH_TOKEN_KEY)

  if (refresh_token) {
    try {
      await api.post('/auth/logout', { refresh_token })
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  clearTokens()
  user.value = null
  answers.value = {}
}

async function refreshProfile() {
  const { data: userData } = await api.get<User>('/profile')
  user.value = userData
}

async function initAuth() {
  loading.value = true
  try {
    // Check both storages for tokens
    const storage =
      localStorage.getItem(REMEMBER_ME_KEY) === 'true'
        ? localStorage
        : sessionStorage
    const token = storage.getItem(TOKEN_KEY)

    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`
      await refreshProfile()
    }
  } catch {
    clearTokens()
  } finally {
    loading.value = false
  }
}

async function forgotPassword(email: string) {
  await api.post('/send-password-reminder', { email })
}

async function resetPassword(
  token: string,
  password: string,
  password_confirmation: string,
) {
  await api.post('/reset-password', { token, password, password_confirmation })
}

export function useAuth(): AuthComposable {
  return {
    user,
    loading,
    login,
    register,
    logout,
    refreshProfile,
    initAuth,
    refreshToken,
    answers,
    updateUser,
    forgotPassword,
    resetPassword,
  }
}
